import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTER_IMAGES } from "../data/content";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

function ChapterBlock({ chapter, flip, onEnquire }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const cta = chapter.ctaType ? { label: chapter.cta, type: chapter.ctaType } : null;

  return (
    <section id={chapter.id} data-testid={`chapter-${chapter.id}`} className="py-20 md:py-32">
      <div className={`max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-12 items-center`}>
        <motion.div {...reveal} className={`col-span-12 md:col-span-5 ${flip ? "md:order-2" : ""}`}>
          <p className="font-mono text-xs tracking-[0.3em] text-bark mb-4" data-testid={`chapter-${chapter.id}-number`}>
            {chapter.number} / {chapter.title.toUpperCase()}
          </p>
          <h2 className="font-serif font-light text-pine text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            {chapter.heading}
          </h2>
          <p className="text-pine/70 text-base md:text-lg leading-relaxed mb-8 max-w-md">{chapter.body}</p>
          <ul className="space-y-2 mb-8">
            {chapter.facts.map((fact) => (
              <li key={fact} className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-pine/60">
                <span className="w-1 h-1 bg-terracotta rounded-full" />
                {fact}
              </li>
            ))}
          </ul>
          {cta && (
            <button
              data-testid={`chapter-${chapter.id}-cta`}
              onClick={() => onEnquire(cta.type)}
              className="bg-pine text-ivory font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-terracotta hover:scale-[1.02] transition-all duration-300"
            >
              {cta.label}
            </button>
          )}
        </motion.div>
        <motion.div
          {...reveal}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className={`col-span-12 md:col-span-7 ${flip ? "md:order-1" : ""}`}
        >
          <div ref={ref} className="relative overflow-hidden aspect-[4/3] group">
            <motion.img
              src={chapter.image}
              alt={chapter.title}
              style={{ y: imgY }}
              className="absolute inset-0 w-full h-[118%] object-cover scale-[1.02] group-hover:scale-[1.06] transition-transform duration-700 ease-out"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-pine/10" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Chapters({ t, onEnquire }) {
  const chapters = t.chapters.map((c, i) => ({ ...c, image: CHAPTER_IMAGES[i] }));
  return (
    <div data-testid="manifesto-chapters">
      {chapters.map((chapter, i) => (
        <ChapterBlock key={chapter.id} chapter={chapter} flip={i % 2 === 1} onEnquire={onEnquire} />
      ))}
    </div>
  );
}
